import axios from "axios";
import { SERVER_URL } from "Constants";
import useAsync from "Hooks/useAsync"
import { CreditReportListItemDetail } from "../../CreditReportList";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import alertToast from "Util/ToastifyUtils";
import EntityList, { IEntityListHeaderProps } from "Views/Components/EntityList/EntityList";
import moment from "moment";
import { Button, Colors, Display, Sizes } from "Views/Components/Button/Button";
import { store } from "Models/Store";
import { GenerateCreditReportStage } from "../../GenerateCreditReport";
import { creditReportStatusOptions } from "Models/Enums";
import HandleEvents from "Util/HandleEvents";

async function generateAndListCreditReport(
	creditReportSubject: {
		lookupValue: string;
		lookupMethod: string;
		countryCode: string;
		entityName: string;
	},
	reportRequestIds: string[]
) {
	try {
		const response = await axios.post<CreditReportListItemDetail[]>(`${SERVER_URL}/api/credit-report/generate`, {
			creditReportSubject,
			reportRequestIds
		});
		alertToast("Credit report generated successfully.", "success");
		return response.data;
	} catch (error) {
		console.error(error);
		alertToast("Sorry, we could not generate the report for this entity. Please try again later.", "error");
		return null;
	}
}

type Props = {
	creditReportSubject: {
		lookupValue: string;
		lookupMethod: string;
		countryCode: string;
		entityName: string;
	},
	reportRequestIds: string[];
	changeStage: (stage: GenerateCreditReportStage) => void;
	customerId?: string;
}

export const GenerateCreditReportCompletion: React.FunctionComponent<Props> = ({
	creditReportSubject,
	reportRequestIds,
	changeStage,
	customerId
}) => {
	const response = useAsync(
		async () => {
			const result = await generateAndListCreditReport(creditReportSubject, reportRequestIds);
			if (!result) {
				changeStage("review");
			}
			return result;
			
		},
		[]
	);

	const getColumns = (): IEntityListHeaderProps<CreditReportListItemDetail>[] => {
		return [
			{
				columnName: "id",
				displayName: "ID",
				value: item => item.id,
				hide: true
			},
			{
				columnName: "reportDateTime",
				displayName: "Date & Time",
				value: item => moment(item.reportDateTime).format('HH:mm DD/MM/YYYY'),
			},
			{
				columnName: "reportProvider",
				displayName: "Provider",
				value: item => item.reportProvider,
			},
			{
				columnName: "reportType",
				displayName: "Report",
				value: item => item.reportType,
			},
			{
				columnName: "entity",
				displayName: "Entity",
				value: item => (
					<>
						<span>{item.entityName}</span>
						<br/>
						<span>{item.entityNo}</span>
					</>
				),
			},
			{
				columnName: "billedTo",
				displayName: "Billed To",
				value: item => item.billedTo,
			},
			{
				columnName: "price",
				displayName: "Price Inc. GST",
				value: item => `$${item.price?.toFixed(2) || '-'}`,
			},
			{
				columnName: "status",
				displayName: "Status",
				value: item => <p className={`report-status ${item.status.toLowerCase()}`}>{creditReportStatusOptions[item.status]}</p>,
			},
			{
				columnName: "generatedBy",
				displayName: "Generated By",
				value: item => item.generatedBy,
			},
			{
				columnName: "downloadLink",
				displayName: "",
				value: item => (
					<>
						{item.status === "COMPLETED" && (
							<Button
								className="file-link client-terms"
								icon={{ icon: 'arrow-right-up', iconPos: 'icon-right' }}
								display={Display.Text}
								colors={Colors.Primary}
								onClick={() => window.open(`${SERVER_URL}/api/credit-report/view/${item.id}`)}
								buttonProps={{ style: { textDecoration: 'none' } }}
							>
								View
							</Button>
						)}
						{item.status !== "COMPLETED" && (
							<span
								{...HandleEvents(() => window.open("mailto:support@accessintell.com"))}
								style={{color: "rgba(246, 70, 79, 1)"}}
							>
								Contact Support
							</span>
						)}
					</>
				),
				sortable: false
			}
		]
	}

	if (reportRequestIds.length === 0) {
		return (
			<div>
				<p>Sorry, we could not generate the report for {creditReportSubject.lookupValue}.</p>
				<p>Please try again later.</p>
			</div>
		);
	}

	return (
		<>
			{response.type === "loading" && (
				<>
					<InlineSpinner />
					<p style={{display: "flex", justifyContent: "center"}}>Generating credit report...</p>
				</>)}
			{response.type === "error" && (
				<>
					<p>Sorry, we could not generate the report for {creditReportSubject.lookupValue}.</p>
					<p>Please try again</p>
				</>
			)}
			{response.data && (
				<>
					<div className="collection-component">
						<EntityList
							collection={response.data}
							columns={getColumns()}
							idColumn="id"
							sortColumn="reportDateTime"
							sortDescending={true}
						/>
					</div>
					<div className="modal-footer">
						<Button
							colors={Colors.Primary}
							sizes={Sizes.Medium}
							display={Display.Outline}
							onClick={() => (!!customerId 
								? store.routerHistory.replace(`${SERVER_URL}/monitor/customers/${customerId}`, { navigateTo: 'reporting'})
								: store.routerHistory.replace(`${SERVER_URL}/hub/credit-reports`))}
						>
							Close
						</Button>
					</div>
				</>
			)}
		</>
	)
}